/**
 * Silnik autozapisu: kolejka zapisow z szeregowaniem per pole. Czysta logika bez Reacta i bez DOM, zeby dalo sie ja
 * przetestowac na sztucznych zegarach (hook useAutozapis tylko ja opakowuje).
 *
 * ZASADY:
 * - Kazde pole ma swoj KLUCZ. Dla jednego klucza w danej chwili leci NAJWYZEJ JEDNO zadanie. Gdy w trakcie lotu pojawi
 *   sie nowsza wartosc, czeka ona na koniec poprzedniego zadania i dopiero wtedy leci: starsza odpowiedz nie moze
 *   nadpisac nowszego tekstu, bo nigdy nie biegna rownolegle.
 * - Do serwera zawsze idzie NAJNOWSZA wartosc. Posrednie (wpisane w trakcie lotu) sa zlewane w jedna.
 * - Zmiana natychmiastowa (opoznienie 0) leci od razu, tekst z opoznieniem (debounce) po przerwie w pisaniu albo od razu
 *   po `splucz` (blur pola).
 * - Nieudany zapis zostaje w kolejce jako blad (z ta sama wartoscia) az do `ponow` albo do nowej wartosci tego pola.
 * - Wartosci pola trzyma KOMPONENT (stan lokalny): silnik ich nie oddaje, wiec zaden zapis ani odpowiedz serwera nie
 *   moze podmienic tego, co uzytkownik pisze.
 * - Opcjonalny magazyn trzyma kopie wartosci, ktorych serwer jeszcze nie przyjal (lib/szkiceRecenzji.ts): wpis powstaje
 *   przy `zaplanuj` i znika po udanym zapisie tej samej wartosci. Awaria magazynu nigdy nie psuje zapisu na serwer.
 */

import type { MagazynSlotow } from "./szkiceRecenzji.js";

export type StanZapisu = "idle" | "saving" | "saved" | "error";

/** Wynik `poczekaj`: pole przyjete w calosci albo blad ostatniego nieudanego zapisu (zostaje do ponowienia). */
export type WynikOczekiwania = { ok: true } | { ok: false; blad: unknown };

export interface MigawkaZapisu {
  stan: StanZapisu;
  /** Chwila ostatniego udanego zapisu (dowolnego pola) albo null, gdy jeszcze nic nie zapisano. */
  zapisanoO: Date | null;
  /** Cos czeka na zapis, leci albo sie nie udalo: powod, dla ktorego zamkniecie karty traci dane. */
  niezapisane: boolean;
  /** Ostatni blad nieudanego zapisu (do klasyfikacji w interfejsie) albo null, gdy bledu nie ma. */
  blad: unknown;
}

interface Slot {
  wartosc: unknown;
  wyslij: (wartosc: unknown) => Promise<unknown>;
  /** Jest wartosc, ktorej serwer jeszcze nie przyjal. */
  brudny: boolean;
  wLocie: boolean;
  /** Opakowanie, bo samym bledem moze byc cokolwiek, takze undefined. */
  blad: { przyczyna: unknown } | null;
  zegar: ReturnType<typeof setTimeout> | null;
}

const MIGAWKA_POCZATKOWA: MigawkaZapisu = { stan: "idle", zapisanoO: null, niezapisane: false, blad: null };

export class KolejkaAutozapisu {
  private readonly sloty = new Map<string, Slot>();
  private zapisanoO: Date | null = null;
  private aktualna: MigawkaZapisu = MIGAWKA_POCZATKOWA;
  private readonly sluchacze = new Set<() => void>();
  private readonly czekajacy = new Map<string, ((wynik: WynikOczekiwania) => void)[]>();

  constructor(
    private readonly teraz: () => Date = () => new Date(),
    private readonly magazyn: MagazynSlotow | null = null,
  ) {}

  /** Stala referencja, dopoki nic sie nie zmieni: tego wymaga useSyncExternalStore. */
  readonly migawka = (): MigawkaZapisu => this.aktualna;

  readonly subskrybuj = (sluchacz: () => void): (() => void) => {
    this.sluchacze.add(sluchacz);
    return () => {
      this.sluchacze.delete(sluchacz);
    };
  };

  /**
   * Zapisuje `wartosc` pod kluczem. `opoznienieMs` > 0 to debounce: kolejne wywolania w tym czasie przesuwaja termin.
   * Opoznienie 0 leci od razu (o ile pole nie ma juz zadania w locie: wtedy zaraz po nim).
   */
  readonly zaplanuj = <T>(klucz: string, wartosc: T, wyslij: (wartosc: T) => Promise<unknown>, opoznienieMs = 0): void => {
    const slot = this.slot(klucz);
    slot.wartosc = wartosc;
    slot.wyslij = wyslij as (w: unknown) => Promise<unknown>;
    slot.brudny = true;
    // Nowa wartosc zastepuje nieudana: jej zapis bedzie osobna proba.
    slot.blad = null;
    this.anulujZegar(slot);
    this.wMagazynie(() => this.magazyn?.zapisz(klucz, wartosc));

    if (opoznienieMs > 0) {
      slot.zegar = setTimeout(() => {
        slot.zegar = null;
        this.uruchom(klucz);
      }, opoznienieMs);
    } else {
      this.uruchom(klucz);
    }
    this.przelicz();
  };

  /** Blur pola: jesli czeka na debounce, wysylamy teraz. */
  readonly splucz = (klucz: string): void => {
    const slot = this.sloty.get(klucz);
    if (!slot || slot.zegar === null) return;
    this.anulujZegar(slot);
    this.uruchom(klucz);
    this.przelicz();
  };

  /**
   * Wysyla wszystko, co czeka: zmiany z debounce ORAZ zapisy, ktore sie nie udaly (zamkniecie widoku, ukrycie karty,
   * zakonczenie przegladu). Ostatnia szansa jest jedna, wiec nieudane tez dostaja jeszcze jedna probe; jesli i ta padnie,
   * wartosc zostaje w magazynie i wroci przy kolejnym wejsciu na rozmowe.
   */
  readonly splukajWszystko = (): void => {
    for (const [klucz, slot] of this.sloty) {
      this.anulujZegar(slot);
      // Nieudany zapis ma brudna wartosc (ta sama), wiec zdjecie bledu wystarczy, zeby `uruchom` ja wyslal.
      slot.blad = null;
      this.uruchom(klucz);
    }
    this.przelicz();
  };

  /** "Sprobuj ponownie": ponawia wszystkie nieudane zapisy. */
  readonly ponow = (): void => {
    for (const [klucz, slot] of this.sloty) {
      if (slot.blad === null) continue;
      slot.blad = null;
      this.uruchom(klucz);
    }
    this.przelicz();
  };

  /**
   * Obietnica rozliczana, gdy pole nie ma juz nic do wyslania: `ok` po udanym zapisie (albo gdy pole nic nie mialo),
   * `blad`, gdy ostatni zapis padl (wartosc zostaje do ponowienia). Nie wysyla niczego sama: wartosc czekajaca na
   * przerwe w pisaniu poleci z zegara, wiec "Gotowe" najpierw robi `splucz`/`splukajWszystko`, a dopiero potem czeka.
   * Pozwala przycisku poczekac na wynik zapisu bez omijania kolejki (wszystko dalej idzie szeregowo per pole).
   */
  readonly poczekaj = (klucz: string): Promise<WynikOczekiwania> =>
    new Promise((rozwiaz) => {
      const gotowy = this.wynikJesliGotowy(klucz);
      if (gotowy) {
        rozwiaz(gotowy);
        return;
      }
      const lista = this.czekajacy.get(klucz) ?? [];
      lista.push(rozwiaz);
      this.czekajacy.set(klucz, lista);
    });

  /** Null: pole jeszcze pracuje (zegar, lot albo nowsza wartosc w drodze). */
  private wynikJesliGotowy(klucz: string): WynikOczekiwania | null {
    const slot = this.sloty.get(klucz);
    if (!slot) return { ok: true };
    if (slot.wLocie || slot.zegar !== null) return null;
    if (slot.blad !== null) return { ok: false, blad: slot.blad.przyczyna };
    return slot.brudny ? null : { ok: true };
  }

  private rozliczCzekajacych(klucz: string): void {
    const lista = this.czekajacy.get(klucz);
    if (!lista) return;
    const wynik = this.wynikJesliGotowy(klucz);
    if (!wynik) return;
    this.czekajacy.delete(klucz);
    for (const rozwiaz of lista) rozwiaz(wynik);
  }

  private slot(klucz: string): Slot {
    let slot = this.sloty.get(klucz);
    if (!slot) {
      slot = {
        wartosc: undefined,
        wyslij: () => Promise.resolve(),
        brudny: false,
        wLocie: false,
        blad: null,
        zegar: null,
      };
      this.sloty.set(klucz, slot);
    }
    return slot;
  }

  /** Magazyn jest dodatkiem: jego wyjatek nie moze przerwac ani zapisu, ani rozliczenia kolejki. */
  private wMagazynie(operacja: () => void): void {
    try {
      operacja();
    } catch {
      // Brak kopii to gorzej, ale nie blad zapisu.
    }
  }

  private anulujZegar(slot: Slot): void {
    if (slot.zegar !== null) {
      clearTimeout(slot.zegar);
      slot.zegar = null;
    }
  }

  private uruchom(klucz: string): void {
    const slot = this.sloty.get(klucz);
    // Zadanie w locie samo wysle nowsza wartosc po swoim zakonczeniu.
    if (!slot || !slot.brudny || slot.wLocie) return;

    const wartosc = slot.wartosc;
    slot.brudny = false;
    slot.wLocie = true;

    let obietnica: Promise<unknown>;
    try {
      obietnica = slot.wyslij(wartosc);
    } catch (przyczyna) {
      obietnica = Promise.reject(przyczyna);
    }

    obietnica.then(
      () => {
        slot.wLocie = false;
        this.zapisanoO = this.teraz();
        // Nowsza wartosc czeka (jej wpis w magazynie juz nadpisal ten): zostaje. Inaczej kopia nie jest juz potrzebna.
        if (!slot.brudny) this.wMagazynie(() => this.magazyn?.usunJesliTa(klucz, wartosc));
        this.poZakonczeniu(klucz, slot);
      },
      (przyczyna: unknown) => {
        slot.wLocie = false;
        if (!slot.brudny) {
          // Nic nowszego nie czeka: ta sama wartosc zostaje do ponowienia.
          slot.brudny = true;
          slot.blad = { przyczyna };
        }
        // Czeka nowsza wartosc: ona zastapi nieudana, wiec bledu nie zapamietujemy.
        this.poZakonczeniu(klucz, slot);
      },
    );
  }

  private poZakonczeniu(klucz: string, slot: Slot): void {
    // Nowsza wartosc dopisana w trakcie lotu: leci od razu, chyba ze czeka na debounce (wtedy poleci z zegara).
    if (slot.brudny && slot.blad === null && slot.zegar === null) this.uruchom(klucz);

    if (!slot.brudny && !slot.wLocie && slot.zegar === null && slot.blad === null) this.sloty.delete(klucz);
    this.rozliczCzekajacych(klucz);
    this.przelicz();
  }

  private przelicz(): void {
    let blad: { przyczyna: unknown } | null = null;
    let pracuje = false;
    for (const slot of this.sloty.values()) {
      if (slot.blad !== null && blad === null) blad = slot.blad;
      if (slot.brudny || slot.wLocie || slot.zegar !== null) pracuje = true;
    }

    const stan: StanZapisu = blad !== null ? "error" : pracuje ? "saving" : this.zapisanoO !== null ? "saved" : "idle";
    const nowa: MigawkaZapisu = {
      stan,
      zapisanoO: this.zapisanoO,
      niezapisane: pracuje || blad !== null,
      blad: blad === null ? null : blad.przyczyna,
    };

    const stara = this.aktualna;
    if (
      stara.stan === nowa.stan &&
      stara.zapisanoO === nowa.zapisanoO &&
      stara.niezapisane === nowa.niezapisane &&
      stara.blad === nowa.blad
    ) {
      return;
    }
    this.aktualna = nowa;
    for (const sluchacz of [...this.sluchacze]) sluchacz();
  }
}
